/**
 * Shared chrome for the masthead's phone icon buttons, so all three are exactly the same height
 * and no single one sets the strip's height on its own. `p-1` round a 20px icon is a 28px
 * target: below the 2.5.5 guidance, which this surface already trades away by standing client
 * decision (every control in view on a ~360px phone, no scrolling) — the same facts stay one tap
 * away in the ⓘ popup, and the full-size controls return from md up.
 *
 * Every table session's masthead builds its icon buttons from this, so a second surface's
 * controls are the same height as this one's without re-deriving the measurement.
 */
export const MASTHEAD_ICON_BUTTON = 'rounded p-1 hover:bg-white/10 xnarrow:p-0.5';

/**
 * The way OUT of a session, whatever it is called — "Leave the Table", "End Session", "Abandon
 * Round". Phones draw it as a brass TAB: a filled rectangle shrink-wrapped round the glyph (client
 * request, 2026-07-28). The round disc it replaced was the tallest thing in the strip and set the
 * row height for everything else; a bare brass glyph fixed the height but read as dim against the
 * felt, because brass is a mid-tone that only carries at full area. `py-1` is deliberately the same
 * 4px the icon buttons above use, so the tab is exactly as tall as they are and still sets no height
 * of its own — all the shrinking comes out of the sides (`px-0.5`), which is what leaves it taller
 * than it is wide. md: gets the full pill, where there is room for it.
 *
 * `xnarrow:px-0.5` rather than `px-0`: at 320px the sideless form measured 20 × 24, and a 20px-wide
 * target is under the 24 × 24 hard floor FR-021a holds every control to (WCAG 2.5.8). Two pixels a
 * side is the whole cost of clearing it, and it leaves the tab exactly as wide as it is tall.
 *
 * `masthead-exit-tab` hands the tab's chrome back to CSS on landscape phones, where the viewport is
 * wider than md: and would otherwise draw the desktop pill (index.css). It works on a `<button>`
 * (End Session, which navigates via a handler) as well as a `<Link>`.
 */
/**
 * Which masthead a table session draws — written once, because every practice surface gates its
 * title, exit text, ⓘ, compact info row and status line the same way (spec 031 FR-026).
 *
 * FULL: `md` and up, or the unfolded fold phone at 749 wide (`fold-ls`), and never the phone row
 * (`narrow`). PHONE: the exact complement. `fold-ls` exists because 749 is under `md` but draws the
 * same row as 832x575, which the client accepted with the full masthead (2026-09-24).
 */
export const MASTHEAD_FULL_INLINE = 'hidden md:inline fold-ls:inline narrow:hidden';
export const MASTHEAD_FULL_BLOCK = 'hidden md:block fold-ls:block narrow:hidden';
export const MASTHEAD_PHONE_BLOCK = 'md:hidden fold-ls:hidden narrow:block';
export const MASTHEAD_PHONE_FLEX = 'md:hidden fold-ls:hidden narrow:flex';

export const MASTHEAD_EXIT_TAB =
  'masthead-exit-tab rounded-[3px] bg-brass px-0.5 py-1 text-on-brass no-underline hover:bg-brass-hover' +
  ' md:rounded-full md:px-4 md:py-2 xnarrow:px-0.5 xnarrow:py-0.5';
